/**
 * Supabase-backed data layer.
 *
 * Auth lives in ./auth.tsx (Supabase Auth). This module covers the
 * `public` schema tables defined in supabase-schema.sql: profiles (role
 * lookups only — auth itself is in auth.tsx), offers, tickets and
 * ticket_messages. RLS policies in that file enforce who can read/write
 * what; these functions assume the current supabase-js session.
 */

import { supabase } from "./supabase";

export type Role = "KUNDE" | "MITARBEITER" | "ADMIN";

// ---------------------------------------------------------------------------
// Offers
// ---------------------------------------------------------------------------

export type Offer = {
  id: string;
  title: string;
  price: string;
  description: string;
  features: string[];
  highlighted: boolean;
};

export async function getOffers(): Promise<Offer[]> {
  const { data, error } = await supabase
    .from("offers")
    .select("id, title, price, description, features, highlighted")
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return (data ?? []) as Offer[];
}

export async function saveOffer(offer: Offer): Promise<void> {
  const { error } = await supabase
    .from("offers")
    .update({
      title: offer.title,
      price: offer.price,
      description: offer.description,
      features: offer.features,
      updated_at: new Date().toISOString(),
    })
    .eq("id", offer.id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Tickets ("Cosmic Chat" + booking inquiries)
// ---------------------------------------------------------------------------

export type TicketStatus = "Offen" | "Beantwortet";

export type TicketMessage = {
  id: string;
  ticket_id: string;
  sender: "customer" | "staff";
  sender_name: string;
  text: string;
  created_at: string;
};

export type Ticket = {
  id: string;
  customer_id: string | null;
  customer_name: string;
  customer_email: string;
  subject: string;
  offer_title: string | null;
  status: TicketStatus;
  created_at: string;
  messages: TicketMessage[];
};

async function attachMessages(tickets: Omit<Ticket, "messages">[]): Promise<Ticket[]> {
  if (tickets.length === 0) return [];
  const { data, error } = await supabase
    .from("ticket_messages")
    .select("*")
    .in(
      "ticket_id",
      tickets.map((t) => t.id)
    )
    .order("created_at", { ascending: true });
  if (error) throw error;
  const byTicket = new Map<string, TicketMessage[]>();
  for (const m of (data ?? []) as TicketMessage[]) {
    const list = byTicket.get(m.ticket_id) ?? [];
    list.push(m);
    byTicket.set(m.ticket_id, list);
  }
  return tickets.map((t) => ({ ...t, messages: byTicket.get(t.id) ?? [] }));
}

/** All tickets visible to the current user — staff see everything (RLS-enforced). */
export async function getAllTickets(): Promise<Ticket[]> {
  const { data, error } = await supabase
    .from("tickets")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return attachMessages((data ?? []) as Omit<Ticket, "messages">[]);
}

/** Tickets for one customer, newest first (RLS restricts to their own rows). */
export async function getTicketsForCustomer(customerId: string): Promise<Ticket[]> {
  const { data, error } = await supabase
    .from("tickets")
    .select("*")
    .eq("customer_id", customerId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return attachMessages((data ?? []) as Omit<Ticket, "messages">[]);
}

/**
 * Bug fix: this previously chained `.select().single()` onto both the
 * `tickets` and `ticket_messages` inserts to read the DB-generated row
 * back. PostgREST enforces the table's SELECT RLS policy on that
 * `RETURNING` read, not just the INSERT policy on the write itself. Both
 * tables' SELECT policies require `auth.uid() = customer_id` (see
 * `tickets_select_own_or_staff` / `ticket_messages_select_own_or_staff` in
 * supabase-schema.sql) — which a guest (no session, `auth.uid()` is null,
 * `customer_id` is null) can never satisfy. Postgres then rolls back the
 * *entire* insert because the RETURNING clause's row-visibility check
 * failed, even though the INSERT policy itself (`customer_id is null`) was
 * satisfied — this surfaced to users as the generic "Nachricht konnte
 * nicht gesendet werden" error on every anonymous contact-form/booking
 * submission (reproduced live against the project's Supabase instance:
 * INSERT ... RETURNING as the anon key returns HTTP 401
 * `new row violates row-level security policy`, while the same INSERT
 * without RETURNING succeeds with 201).
 *
 * Fix: generate the id client-side (`crypto.randomUUID()`, available in
 * all evergreen browsers this SPA targets) and insert without `.select()`
 * (`Prefer: return=minimal`, which never touches the SELECT policy at
 * all) — we already know every field we need to construct the returned
 * `Ticket`/`TicketMessage` objects from what was passed in, so no
 * read-back is actually necessary. This works identically for logged-in
 * customers and guests, since it no longer depends on `auth.uid()` at all
 * for the write path.
 */
export async function createTicket(input: {
  customerId: string | null;
  customerName: string;
  customerEmail: string;
  subject: string;
  offerTitle: string | null;
  firstMessage: string;
}): Promise<Ticket> {
  const ticketId = crypto.randomUUID();
  const createdAt = new Date().toISOString();

  const { error: ticketError } = await supabase.from("tickets").insert({
    id: ticketId,
    customer_id: input.customerId,
    customer_name: input.customerName,
    customer_email: input.customerEmail,
    subject: input.subject,
    offer_title: input.offerTitle,
    status: "Offen",
    created_at: createdAt,
  });
  if (ticketError) throw ticketError;

  const messageId = crypto.randomUUID();
  const messageCreatedAt = new Date().toISOString();
  const { error: messageError } = await supabase.from("ticket_messages").insert({
    id: messageId,
    ticket_id: ticketId,
    sender: "customer",
    sender_name: input.customerName,
    text: input.firstMessage,
    created_at: messageCreatedAt,
  });
  if (messageError) throw messageError;

  const message: TicketMessage = {
    id: messageId,
    ticket_id: ticketId,
    sender: "customer",
    sender_name: input.customerName,
    text: input.firstMessage,
    created_at: messageCreatedAt,
  };
  return {
    id: ticketId,
    customer_id: input.customerId,
    customer_name: input.customerName,
    customer_email: input.customerEmail,
    subject: input.subject,
    offer_title: input.offerTitle,
    status: "Offen",
    created_at: createdAt,
    messages: [message],
  };
}

export async function addTicketMessage(
  ticketId: string,
  sender: "customer" | "staff",
  senderName: string,
  text: string
): Promise<void> {
  const { error: msgError } = await supabase
    .from("ticket_messages")
    .insert({ ticket_id: ticketId, sender, sender_name: senderName, text });
  if (msgError) throw msgError;

  if (sender === "staff") {
    const { error: statusError } = await supabase
      .from("tickets")
      .update({ status: "Beantwortet" })
      .eq("id", ticketId);
    if (statusError) throw statusError;
  }
}

export async function setTicketStatus(ticketId: string, status: TicketStatus): Promise<void> {
  const { error } = await supabase.from("tickets").update({ status }).eq("id", ticketId);
  if (error) throw error;
}

/** Live updates for one ticket's messages — used to sync Admin ↔ Kundenportal. */
export function subscribeToTicketMessages(ticketId: string, onInsert: (msg: TicketMessage) => void) {
  const channel = supabase
    .channel(`ticket-messages-${ticketId}`)
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "ticket_messages", filter: `ticket_id=eq.${ticketId}` },
      (payload) => onInsert(payload.new as TicketMessage)
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

// ---------------------------------------------------------------------------
// Page images (Admin-managed CMS overrides, backed by Supabase Storage)
// ---------------------------------------------------------------------------

export const IMAGE_BUCKET = "website-images";

export type PageImage = {
  id: string;
  page_path: string;
  section_name: string;
  image_url: string;
  alt_text: string;
  updated_at: string;
};

/** All admin-set image overrides, for the Admin "Medien & Seitenbilder" tab. */
export async function getAllPageImages(): Promise<PageImage[]> {
  const { data, error } = await supabase
    .from("page_images")
    .select("id, page_path, section_name, image_url, alt_text, updated_at")
    .order("page_path", { ascending: true });
  if (error) throw error;
  return (data ?? []) as PageImage[];
}

/** One override lookup, used by <DynamicImage> at render time. */
export async function getPageImage(pagePath: string, sectionName: string): Promise<PageImage | null> {
  const { data, error } = await supabase
    .from("page_images")
    .select("id, page_path, section_name, image_url, alt_text, updated_at")
    .eq("page_path", pagePath)
    .eq("section_name", sectionName)
    .maybeSingle();
  if (error) throw error;
  return (data as PageImage) ?? null;
}

/**
 * Uploads a file to the public `website-images` Storage bucket and upserts
 * the page_images row pointing at its public URL. RLS restricts both the
 * Storage write and the table write to ADMIN (see supabase-schema.sql).
 */
export async function setPageImage(input: {
  pagePath: string;
  sectionName: string;
  file: File;
  altText: string;
}): Promise<PageImage> {
  const ext = input.file.name.split(".").pop() || "jpg";
  const path = `${input.pagePath.replace(/^\/+/, "") || "home"}/${input.sectionName}-${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(IMAGE_BUCKET)
    .upload(path, input.file, { upsert: true, cacheControl: "3600" });
  if (uploadError) throw uploadError;

  const { data: publicUrlData } = supabase.storage.from(IMAGE_BUCKET).getPublicUrl(path);

  const { data, error } = await supabase
    .from("page_images")
    .upsert(
      {
        page_path: input.pagePath,
        section_name: input.sectionName,
        image_url: publicUrlData.publicUrl,
        alt_text: input.altText,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "page_path,section_name" }
    )
    .select()
    .single();
  if (error) throw error;
  return data as PageImage;
}

/**
 * Resets an image slot back to its built-in default placeholder by
 * deleting the page_images row (and its Storage object, best-effort).
 * The <DynamicImage> component then falls back to `defaultImage` again.
 */
export async function resetPageImage(pagePath: string, sectionName: string): Promise<void> {
  const existing = await getPageImage(pagePath, sectionName);
  const { error } = await supabase
    .from("page_images")
    .delete()
    .eq("page_path", pagePath)
    .eq("section_name", sectionName);
  if (error) throw error;

  if (existing) {
    const marker = `/storage/v1/object/public/${IMAGE_BUCKET}/`;
    const idx = existing.image_url.indexOf(marker);
    if (idx !== -1) {
      const objectPath = existing.image_url.slice(idx + marker.length);
      // Best-effort cleanup; a failed Storage delete must not block the
      // page_images row removal that already succeeded above.
      await supabase.storage.from(IMAGE_BUCKET).remove([objectPath]).catch(() => {});
    }
  }
}

// ---------------------------------------------------------------------------
// Dashboard data (real Supabase reads used by the Admin overview)
// ---------------------------------------------------------------------------

export type ProfileRow = {
  id: string;
  role: Role;
  created_at: string;
};

/**
 * All profiles (id/role/created_at only) for staff/admin dashboard stats.
 * RLS (`profiles_select_own_or_staff`) allows this for MITARBEITER/ADMIN.
 */
export async function getAllProfiles(): Promise<ProfileRow[]> {
  const { data, error } = await supabase.from("profiles").select("id, role, created_at");
  if (error) throw error;
  return (data ?? []) as ProfileRow[];
}

export type TicketSummary = {
  id: string;
  status: TicketStatus;
  offer_title: string | null;
  created_at: string;
};

/** Lightweight ticket read (no message bodies) for KPI/business stats. */
export async function getTicketsSummary(): Promise<TicketSummary[]> {
  const { data, error } = await supabase
    .from("tickets")
    .select("id, status, offer_title, created_at");
  if (error) throw error;
  return (data ?? []) as TicketSummary[];
}

// ---------------------------------------------------------------------------
// Team management (ADMIN only — enforced by RLS on staff_invites/profiles)
// ---------------------------------------------------------------------------

export type StaffProfile = {
  id: string;
  name: string;
  email: string;
  role: Role;
};

/** Existing staff/admin accounts (customers are excluded by RLS-visible rows + filter). */
export async function getStaffProfiles(): Promise<StaffProfile[]> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, name, email, role")
    .neq("role", "KUNDE");
  if (error) throw error;
  return (data ?? []) as StaffProfile[];
}

/**
 * Pre-authorize an email for a staff/admin role. The person must then sign
 * up themselves at /login with that exact email — handle_new_user (see
 * supabase-schema.sql) assigns the invited role on signup instead of the
 * KUNDE default. The browser can never create other users' accounts
 * directly without the service_role key, which must stay server-side.
 */
export async function inviteStaff(email: string, role: "MITARBEITER" | "ADMIN"): Promise<void> {
  const { error } = await supabase.from("staff_invites").insert({ email, role });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Saved charts (Vedic calculator results) — see supabase-schema.sql section 11
// ---------------------------------------------------------------------------

/**
 * Chart calculation payload stored per saved chart. Mirrors the shape
 * produced by src/lib/calculator/service.ts, which wraps the real
 * ephemeris calculation in src/lib/ephemeris/chart.ts (astronomy-engine +
 * Lahiri ayanamsha — see that module's header for precision notes and
 * docs/reference-calculator.md for the verification methodology).
 */
export type ChartData = {
  lagna: { name: string; western: string };
  rashi: { name: string; western: string };
  nakshatra: string;
  /** Navamsa (D9) moon sign — the most commonly requested divisional chart
   *  per docs/reference-calculator.md's comparison to the reference calc. */
  navamsaRashi: { name: string; western: string };
  currentDasha: string;
  dashaLordSanskrit: string;
};

export type SavedChart = {
  id: string;
  user_id: string;
  label: string;
  full_name: string;
  birth_date: string; // "YYYY-MM-DD"
  birth_time: string; // "HH:MM:SS"
  birth_place: string;
  latitude: number | null;
  longitude: number | null;
  timezone: string | null;
  chart_data: ChartData;
  created_at: string;
};

export type NewSavedChart = Omit<SavedChart, "id" | "user_id" | "created_at">;

/** All charts owned by the current user (RLS: own rows only). */
export async function getSavedCharts(userId: string): Promise<SavedChart[]> {
  const { data, error } = await supabase
    .from("saved_charts")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as SavedChart[];
}

/**
 * Error thrown when the account's chart_quota (see supabase-schema.sql
 * enforce_chart_quota_trigger) rejects the insert. The frontend can catch
 * this specifically to show an upgrade prompt instead of a generic error.
 */
export class ChartQuotaExceededError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ChartQuotaExceededError";
  }
}

/**
 * Saves a new chart for the given user. The server-side
 * enforce_chart_quota_trigger is the real quota boundary (Postgres raises
 * `chart_quota_exceeded: ...` with errcode P0001) — this function just
 * translates that into a typed error the UI can branch on. Any
 * pre-check done in src/lib/calculator/service.ts is a UX nicety only.
 */
export async function saveChart(userId: string, chart: NewSavedChart): Promise<SavedChart> {
  const { data, error } = await supabase
    .from("saved_charts")
    .insert({
      user_id: userId,
      label: chart.label,
      full_name: chart.full_name,
      birth_date: chart.birth_date,
      birth_time: chart.birth_time,
      birth_place: chart.birth_place,
      latitude: chart.latitude,
      longitude: chart.longitude,
      timezone: chart.timezone,
      chart_data: chart.chart_data,
    })
    .select()
    .single();
  if (error) {
    if (error.message.includes("chart_quota_exceeded")) {
      throw new ChartQuotaExceededError(error.message);
    }
    throw error;
  }
  return data as SavedChart;
}

export async function deleteSavedChart(chartId: string): Promise<void> {
  const { error } = await supabase.from("saved_charts").delete().eq("id", chartId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Chart management (ADMIN only — enforced by RLS saved_charts_admin_all /
// profiles_update_admin in supabase-schema.sql)
// ---------------------------------------------------------------------------

export type ChartOwnerSummary = {
  user_id: string;
  name: string;
  email: string;
  role: Role;
  chart_quota: number | null;
  chart_count: number;
};

/**
 * All saved charts across every account, for the Admin "Chart-Verwaltung"
 * overview. RLS (saved_charts_admin_all) allows this only for ADMIN.
 */
export async function getAllSavedCharts(): Promise<SavedChart[]> {
  const { data, error } = await supabase
    .from("saved_charts")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as SavedChart[];
}

/**
 * Per-customer chart counts + current quota, joined client-side from two
 * RLS-scoped reads (profiles rows visible to staff/admin via
 * profiles_select_own_or_staff; saved_charts rows visible to admin via
 * saved_charts_admin_all).
 */
export async function getChartOwnerSummaries(): Promise<ChartOwnerSummary[]> {
  const [{ data: profiles, error: profilesError }, { data: charts, error: chartsError }] = await Promise.all([
    supabase.from("profiles").select("id, name, email, role, chart_quota"),
    supabase.from("saved_charts").select("user_id"),
  ]);
  if (profilesError) throw profilesError;
  if (chartsError) throw chartsError;

  const countByUser = new Map<string, number>();
  for (const row of (charts ?? []) as Array<{ user_id: string }>) {
    countByUser.set(row.user_id, (countByUser.get(row.user_id) ?? 0) + 1);
  }

  return ((profiles ?? []) as Array<{ id: string; name: string; email: string; role: Role; chart_quota: number | null }>).map(
    (p) => ({
      user_id: p.id,
      name: p.name,
      email: p.email,
      role: p.role,
      chart_quota: p.chart_quota,
      chart_count: countByUser.get(p.id) ?? 0,
    })
  );
}

/**
 * Sets a customer's chart_quota (null = unlimited). Requires the caller to
 * be signed in as ADMIN — enforced server-side by the profiles_update_admin
 * RLS policy (supabase-schema.sql section 11), independent of the existing
 * protect_profile_role_trigger which only guards the `role` column.
 */
export async function setChartQuota(userId: string, quota: number | null): Promise<void> {
  const { error } = await supabase.from("profiles").update({ chart_quota: quota }).eq("id", userId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Recent activity feed (Admin/staff overview — "Neueste Aktivitäten")
// ---------------------------------------------------------------------------

export type ActivityEvent = {
  id: string;
  type: "signup" | "ticket" | "chart";
  at: string; // ISO timestamp
  title: string;
  detail?: string;
};

/**
 * Merges the most recent signups (profiles), support/booking tickets, and
 * saved charts into one chronological feed for the Admin overview's
 * "Neueste Aktivitäten" section.
 *
 * RLS note: profiles and tickets are readable by any MITARBEITER/ADMIN
 * (is_staff()), so signup/ticket events show for both roles. saved_charts
 * is admin-only (saved_charts_admin_all) — for a signed-in MITARBEITER,
 * Postgres RLS silently returns zero rows for that query (not an error),
 * so a staff account simply sees a feed without chart events rather than
 * a broken page.
 */
export async function getRecentActivity(limit = 10): Promise<ActivityEvent[]> {
  const [profilesRes, ticketsRes, chartsRes] = await Promise.all([
    supabase.from("profiles").select("id, name, email, role, created_at").order("created_at", { ascending: false }).limit(limit),
    supabase.from("tickets").select("id, customer_name, subject, offer_title, created_at").order("created_at", { ascending: false }).limit(limit),
    supabase.from("saved_charts").select("id, label, full_name, created_at").order("created_at", { ascending: false }).limit(limit),
  ]);
  if (profilesRes.error) throw profilesRes.error;
  if (ticketsRes.error) throw ticketsRes.error;
  if (chartsRes.error) throw chartsRes.error;

  const events: ActivityEvent[] = [
    ...((profilesRes.data ?? []) as Array<{ id: string; name: string; email: string; role: Role; created_at: string }>).map(
      (p) => ({
        id: `signup-${p.id}`,
        type: "signup" as const,
        at: p.created_at,
        title: `Neue Registrierung: ${p.name}`,
        detail: `${p.email} · ${p.role}`,
      })
    ),
    ...((ticketsRes.data ?? []) as Array<{ id: string; customer_name: string; subject: string; offer_title: string | null; created_at: string }>).map(
      (t) => ({
        id: `ticket-${t.id}`,
        type: "ticket" as const,
        at: t.created_at,
        title: `Neue Anfrage: ${t.subject}`,
        detail: t.offer_title ? `${t.customer_name} · ${t.offer_title}` : t.customer_name,
      })
    ),
    ...((chartsRes.data ?? []) as Array<{ id: string; label: string; full_name: string; created_at: string }>).map(
      (c) => ({
        id: `chart-${c.id}`,
        type: "chart" as const,
        at: c.created_at,
        title: `Neues Horoskop: ${c.label}`,
        detail: c.full_name,
      })
    ),
  ];

  return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).slice(0, limit);
}

// ---------------------------------------------------------------------------
// chart_requests — geo/demographic metadata from the public calculator
// ---------------------------------------------------------------------------

/**
 * Fire-and-forget insert called from BirthChartForm after a successful chart
 * computation. Captures the browser language, detected timezone, and the
 * user-selected birth-country so the admin "Geografie & Demografie" section
 * can show live data. Failures are swallowed — they must never block the
 * user's chart result.
 */
export async function insertChartRequest(data: {
  language: string;
  timezone: string;
  country_code: string;
}): Promise<void> {
  const { error } = await supabase.from("chart_requests").insert(data);
  if (error) {
    // Non-fatal telemetry — log in dev, ignore in prod.
    // eslint-disable-next-line no-console
    console.warn("[insertChartRequest] skipped:", error.message);
  }
}

export type GeoStats = {
  topCountries: { code: string; count: number }[];
  languages: { name: string; count: number }[];
  timezones: { name: string; count: number }[];
  total: number;
};

/**
 * Reads all rows from chart_requests (STAFF/ADMIN only via RLS) and
 * aggregates them into top-5 lists for the geo/demographic dashboard section.
 * Aggregation is done in JS to avoid needing DB-side GROUP BY functions.
 */
export async function getGeoStats(): Promise<GeoStats> {
  const { data, error } = await supabase
    .from("chart_requests")
    .select("language, timezone, country_code");
  if (error) throw error;

  type Row = { language: string | null; timezone: string | null; country_code: string | null };
  const rows = (data ?? []) as Row[];

  const tally = (key: keyof Row) => {
    const m = new Map<string, number>();
    for (const r of rows) {
      const v = r[key];
      if (v) m.set(v, (m.get(v) ?? 0) + 1);
    }
    return [...m.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));
  };

  return {
    topCountries: tally("country_code").map(({ name, count }) => ({ code: name, count })),
    languages: tally("language"),
    timezones: tally("timezone"),
    total: rows.length,
  };
}
